import type { APIService } from './interfaces/services';
import { uploadAttachment } from './services/AttachmentService';
import type {
  AsyncMessageStatusResponse,
  AsyncMessageSubmitResponse,
  AttachmentMeta,
  CreateNewConversationPayload,
  CreateNewConversationResponse,
  GetConversationsResponse,
  GetSingleConversationResponse,
  PostMessagePayload,
  PostMessageResponse,
} from './types';

// Hook that accepts services as parameters for dependency injection
export const useConversationApi = (apiService: APIService) => {
  const abortController = new AbortController();

  const createConversationUrl = () => '/api/v1/assistant/conversations/';
  const getConversationUrl = (id: string) => `/api/v1/assistant/conversations/${id}/`;
  const getAllConversationsUrl = () => '/api/v1/assistant/conversations/';
  const postMessageUrl = (id: string | null) => `/api/v1/assistant/conversations/${id}/messages/`;

  const createConversation = async (payload: CreateNewConversationPayload): Promise<CreateNewConversationResponse> => {
    return apiService.post<CreateNewConversationResponse>(createConversationUrl(), payload);
  };

  const sendMessage = async (conversationId: string, payload: PostMessagePayload): Promise<PostMessageResponse> => {
    return apiService.post<PostMessageResponse>(postMessageUrl(conversationId), payload);
  };

  const sendMessageAsync = async (
    conversationId: string,
    payload: PostMessagePayload,
  ): Promise<AsyncMessageSubmitResponse> => {
    return apiService.post<AsyncMessageSubmitResponse>(
      `/api/v1/assistant/conversations/${conversationId}/messages/async/`,
      payload,
    );
  };

  const getMessageStatus = async (conversationId: string, requestId: string): Promise<AsyncMessageStatusResponse> => {
    return apiService.get<AsyncMessageStatusResponse>(
      `/api/v1/assistant/conversations/${conversationId}/messages/status/`,
      { request_id: requestId },
    );
  };

  const getConversations = async (limit?: number, offset?: number): Promise<GetConversationsResponse> => {
    return apiService.get<GetConversationsResponse>(getAllConversationsUrl(), { limit, offset });
  };

  const getSingleConversation = async (id: string): Promise<GetSingleConversationResponse> => {
    return apiService.get<GetSingleConversationResponse>(getConversationUrl(id));
  };

  const submitFeedback = async (
    conversationId: string,
    messageId: string,
    feedback: number | null,
    feedbackResponse?: string | null,
  ): Promise<PostMessageResponse> => {
    const payload: Record<string, unknown> = { feedback };
    if (feedbackResponse !== undefined) {
      payload.feedback_response = feedbackResponse;
    }
    return apiService.post<PostMessageResponse>(
      `/api/v1/assistant/conversations/${conversationId}/messages/${messageId}/feedback/`,
      payload,
    );
  };

  const createAttachment = async (
    file: File,
    conversationId: string,
    onProgress: (pct: number) => void,
    signal: AbortSignal,
  ): Promise<AttachmentMeta> => {
    return uploadAttachment(file, conversationId, apiService, onProgress, signal);
  };

  return {
    createConversation,
    sendMessage,
    sendMessageAsync,
    getMessageStatus,
    getConversations,
    getSingleConversation,
    submitFeedback,
    createAttachment,
    abortController,
  };
};
